import React, {useEffect, useMemo, useRef, useState} from 'react';
import { useHistory } from 'react-router-dom'
import "../scss/SupportBots.scss"
import { useIntl } from "react-intl";
import {
    Bot,
    Plus,
    PanelTopClose,
    PanelTopOpen,
} from 'lucide-react';
import {Link} from "react-router-dom";
import {useSupportBotContext, useSupportBotData} from "./SupportBotEditor/SupportBotData";
import {useCreateBotVersion} from "./SupportBotEditor/hooks/useCreateBotVersion";
import {BotCard} from "../components/BotCard/BotCard";

export default function SupportBots() {
    const {
        isLoading,
        isError,
        isSuccess,
        data: {
            supportBots,
            supportTopics,
        } = {} // Provide default empty object to avoid destructuring errors
    } = useSupportBotData();

    const [searchValue, setSearchValue] = useState("")
    const [allExpanded, setAllExpanded] = useState(false)
    const intl = useIntl()

    useEffect(() => {
        document.title = intl.formatMessage({
            id: "supportBots",
            defaultMessage: "Support Bots",
        }) + " | Annie Admin"
    }, [intl])

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchValue(value)
    }

    return (
        <div className="content">
            <div className="header-container">
                <h1>
                    <Bot size={24} strokeWidth={2} />
                    {intl.formatMessage({
                        id: 'supportBots',
                        defaultMessage: 'Support Bots'
                    })}
                </h1>
            </div>
            <div className={"toolbar support-bots-toolbar"}>
                <input type={"search"}
                       value={searchValue}
                       onChange={handleSearchChange}
                       placeholder={intl.formatMessage({
                           defaultMessage: 'Search bots',
                           id: 'searchBots'
                       })}
                />
                <Link to={`/support-bot/new`} className={"create-new button"}>
                    <Plus size={16} strokeWidth={3} />
                    {intl.formatMessage(
                        {
                            id: 'createNewSupportBot',
                            defaultMessage: 'New Support Bot',
                        }
                    )}
                </Link>
                <button className={"icon-button toggle"}
                        onClick={() => setAllExpanded(!allExpanded)}
                        title={allExpanded ? intl.formatMessage({
                            id: 'collapseAll',
                            defaultMessage: 'Collapse All'
                        }) : intl.formatMessage({
                            id: 'expandAll',
                            defaultMessage: 'Expand All'
                        })}
                >
                    {allExpanded ?
                        <PanelTopClose /> :
                        <PanelTopOpen />
                    }
                </button>
            </div>

            {isLoading && (
                <div className="loading-container">
                    <p>{intl.formatMessage({
                        id: 'loadingBots',
                        defaultMessage: 'Loading support bots...'
                    })}</p>
                </div>
            )}

            {isError && (
                <div className="error-container">
                    <p>{intl.formatMessage({
                        id: 'errorLoadingBots',
                        defaultMessage: 'Error loading support bots. Please try again later.'
                    })}</p>
                    <button
                        className="retry-button"
                        onClick={() => window.location.reload()}
                    >
                        {intl.formatMessage({
                            id: 'retry',
                            defaultMessage: 'Retry'
                        })}
                    </button>
                </div>
            )}

            {isSuccess && (
                <SupportBotsContent
                    supportBots={supportBots}
                    searchValue={searchValue}
                    allExpanded={allExpanded}
                />
            )}
        </div>
    )
}

// Separated component that handles the supportBots data
function SupportBotsContent({ supportBots, searchValue, allExpanded }) {
    const [versionFilters, setVersionFilters] = useState({})
    const [expandedGroups, setExpandedGroups] = useState({})
    const intl = useIntl()

    // Initialize version filters with DEFAULT version for each group
    useEffect(() => {
        const initialVersions = {};
        const botsArray = Object.values(supportBots);

        botsArray.forEach(bot => {
            const versionGroup = bot.config?.versionGroup;
            const version = bot.config?.version;

            if (versionGroup) {
                if (!initialVersions[versionGroup]) {
                    initialVersions[versionGroup] = version;
                }
                if (version === "DEFAULT") {
                    initialVersions[versionGroup] = "DEFAULT";
                }
            }
        });

        setVersionFilters(initialVersions);
    }, [supportBots]);


    const botGroups = useMemo(() => {
        const groups = {};

        Object.entries(supportBots).forEach(([key, bot]) => {
            const groupKey = bot.config?.versionGroup || key;

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    bots: [],
                    latestUpdate: null,
                    groupKey: groupKey
                };
            }

            groups[groupKey].bots.push({ key, bot });

            const botDate = new Date(bot.updated);
            if (!groups[groupKey].latestUpdate || botDate > groups[groupKey].latestUpdate) {
                groups[groupKey].latestUpdate = botDate;
            }
        });

        // Sort groups by latest update
        return Object.values(groups).sort((a, b) => b.latestUpdate - a.latestUpdate);
    }, [supportBots]);

    // Filter bots based on search and version filters
    const filteredBotData = botGroups
        .map(group => {
            const botToShow = group.bots.find(({ bot }) => {
                if (bot.config?.versionGroup) {
                    return versionFilters[bot.config.versionGroup] === bot.config.version;
                }
                return true;
            });

            return botToShow;
        })
        .filter(item => {
            if (!item) return false;

            const { bot } = item;
            return bot?.config?.name &&
                bot.config.name.toLowerCase().includes(searchValue.toLowerCase());
        });

    const handleVersionChange = (versionGroup, version) => {
        setVersionFilters(prev => ({
            ...prev,
            [versionGroup]: version
        }));
    }

    const toggleExpanded = (botId, versionGroup, isExpanded) => {
        const toggleId = versionGroup || botId;
        setExpandedGroups(prev => ({
            ...prev,
            [toggleId]: isExpanded
        }));
    }

    return (
        <div className="support-bots">
            {filteredBotData.length === 0 ? (
                <div className="no-results">
                    <p>{intl.formatMessage({
                        id: 'noBotsFound',
                        defaultMessage: 'No support bots match your search.'
                    })}</p>
                </div>
            ) : (
                filteredBotData.map(({ key, bot } ) => {
                    // Determine if this bot/group should be expanded
                    const groupId = bot.config?.versionGroup || bot?.id
                    const isExpanded = allExpanded || expandedGroups[groupId] || false

                    return <BotBlockWrapper
                        bot={bot}
                        botId={bot?.id}
                        key={key}
                        isExpanded={isExpanded}
                        onVersionChange={handleVersionChange}
                        onToggleExpanded={toggleExpanded}
                    />
                })
            )}
        </div>
    );
}

function BotBlockWrapper({ bot, botId, isExpanded, onVersionChange, onToggleExpanded }) {
    const history = useHistory();
    const { data: { users, supportTopicCodes } } = useSupportBotContext();
    const { createNewVersion } = useCreateBotVersion();

    return (
        <BotCard
            data={bot}
            botId={botId}
            mode="view"
            isExpanded={isExpanded}
            onHeaderClick={(id) => history.push(`/support-bot/${id}`)}
            onToggleExpanded={onToggleExpanded}
            onVersionChange={onVersionChange}
            onCreateVersion={createNewVersion}
            userDataSource={users}
            topicDataSource={supportTopicCodes}
        />
    );
}